import _findIndex from 'lodash/findIndex';

// imports
import SessionModel from '../../models/session/SessionModel';
import SessionCheckModel from '../../models/session/SessionCheckModel';
import UserModel from '../../models/UserModel';
import c_UserModel from '../../models/community/UserModel';
import ProCommunityModel from '../../models/community/CommunityModel';
import GdprUserModel from '../../models/session/GdprUserModel';
import TermsOfUseModel from '../../models/session/TermsOfUseModel';
import AccessCheckModel from '../../models/session/AccessCheckModel';
import NotificationModel from '../../models/session/NotificationsModel';
import { setSuccessMessage } from '../../utils/setGlobalMessage';

import Config from '../../Config';
import Pusher from 'pusher-js';

import { notify } from '../../utils/notify.js';

const HEATMAP_READY_TITLE = 'Je actieve vrijwilligersheatmap staat klaar.';

function parseNotificationUrl(linkUrl) {
    if (!linkUrl) return null;
    try {
        return new URL(linkUrl, window.location.origin);
    } catch (error) {
        return null;
    }
}

function isSameVolunteerMapView(target) {
    if (!target) return false;
    return target.pathname === window.location.pathname && target.search === window.location.search;
}

function heatmapRouteFromUrl(target) {
    if (!target) {
        return `${Config.routes.volunteers.map}#heatmap`;
    }
    return `${target.pathname}${target.search}#heatmap`;
}

function resolveBinsQr(proCommunity, modules) {
    const explicit = [proCommunity && proCommunity.binsQr, proCommunity && proCommunity.bins_qr, proCommunity && proCommunity.qrEnabled].find((value) => value !== undefined && value !== null);

    if (explicit !== undefined) return !!explicit;
    return modules.includes('bins');
}

function isForbidden(error) {
    const status = error?.response?.status ?? error?.status;
    return status === 403;
}

function applyProSession(user, proUser, proCommunity, uid) {
    const modules = proUser.modules || [];

    user.set({
        id: proUser.uuid || '',
        uid: Number(uid) || 0,
        name: proUser.fullName || '',
        first_name: proUser.firstName || '',
        last_name: proUser.lastName || '',
        email: proUser.emailAddress || '',
        bin_management: modules.includes('bins'),
        volunteer_permission: modules.includes('volunteers'),
        changed: new Date(),
    });

    const communityUpdate = {
        feature_bins: modules.includes('bins'),
        feature_incidents: modules.includes('incidents'),
        feature_volunteers: modules.includes('volunteers'),
        feature_monitoring: modules.includes('monitoring'),
        bins_qr: resolveBinsQr(proCommunity, modules),
        changed: new Date(),
    };

    if (!proCommunity) {
        user.community.set(communityUpdate);
        return;
    }

    const postalCodes = Array.isArray(proCommunity.postalCodes) ? proCommunity.postalCodes : [];

    user.community.set({
        ...communityUpdate,
        id: proCommunity.uuid || '',
        title: proCommunity.title || '',
        email: proCommunity.emailAddress || '',
    });

    user.community.postcodes.clear();
    user.community.postcodes.set(
        postalCodes.map((postalCode) => ({
            id: String(postalCode.postalCode || postalCode.code || ''),
            title: postalCode.title || '',
            code: Number(postalCode.postalCode || postalCode.code) || 0,
        }))
    );
}

function refetchHeatmapIfPossible(dispatch) {
    if (window.vueStore?.state?.activitiesDM?.collection) {
        return dispatch('activitiesDM/fetchData', false, { root: true });
    }
    return Promise.resolve();
}

function notifyHeatmapReady(data, heatmapUrl) {
    notify({
        group: 'mms-notifications',
        type: 'success',
        text: `${data.title} ${data.message || ''}`.trim(),
        data: {
            hasLink: true,
            type: 'heatmap',
            urlText: 'Naar de heatmap',
            url: heatmapUrl,
        },
    });
}

function notificationPayload(payload) {
    let data = payload;
    if (typeof data === 'string') {
        try {
            data = JSON.parse(data);
        } catch (error) {
            return { title: '', message: data, severity: 'success' };
        }
    }
    if (!data || typeof data !== 'object') return {};

    const nested = data.data;
    if (nested && typeof nested === 'object' && (nested.title || nested.message || nested.link)) {
        data = nested;
    }

    const link = data.link && typeof data.link === 'object' ? { ...data.link } : data.link;
    return {
        title: data.title || '',
        message: typeof data.message === 'string' ? data.message : data.message == null ? '' : String(data.message),
        severity: data.severity || data.status || '',
        link,
    };
}

function showExportNotification(data) {
    const message = typeof data.message === 'string' ? data.message.replace(/&lt;/g, '<').replace(/&gt;/g, '>') : '';
    const text = `${data.title || ''} ${message}`.trim();
    const link = data.link && typeof data.link === 'object' ? data.link : null;
    let url = '';

    if (typeof data.link === 'string') {
        url = data.link;
    } else if (link?.url) {
        url = String(link.url);
        try {
            url = decodeURI(url);
        } catch (error) {
            // keep the original url if it is not a valid encoded URI
        }
        url = url.replace('backUrl=/', 'backUrl=%2F');
    }

    const severity = String(data.severity || '').toLowerCase();
    const type = severity === 'error' || severity === 'danger' ? 'error' : 'success';

    notify.close('xlsxPusher');
    notify({
        group: 'mms-notifications',
        type,
        text: text || (type === 'error' ? 'Het exporteren van de data is mislukt.' : 'Export klaar.'),
        data: url
            ? {
                  hasLink: true,
                  url,
                  urlText: link?.title || 'Download',
              }
            : {},
    });
}

// private statics

// module definition
export default {
    namespaced: true,

    // state
    state() {
        return {
            loading: false,
            authenticated: false,
            checker: new SessionCheckModel(),
            model: new SessionModel({
                autoLoad: true,
            }),
            user: new UserModel({
                include: ['community', 'field_user_community.field_community_api_partners'],
            }),
            c_user: new c_UserModel(),
            gdprUser: new GdprUserModel(),
            rememberLoggedIn: false,
            hideMeasurementIntro: false,
            incidentCreateAccess: new AccessCheckModel({ type: 'incidentCreateAccess' }),
            incidentUpdateAccess: new AccessCheckModel({ type: 'incidentEditPermission' }),
            momoApiAcces: new AccessCheckModel({ type: 'momoApi' }),
            exportReportsApiAcces: new AccessCheckModel({ type: 'exportReportsApi' }),
            groupAdminAccess: new AccessCheckModel({ type: 'groupAdminAccess' }),
            userAdminAccess: new AccessCheckModel({ type: 'userAdminAccess' }),
            // termsOfUse: new TermsOfUseModel(),
            notifcations: new NotificationModel(),
        };
    },

    // getters
    getters: {
        model: ({ model }) => model,
        user: ({ user }) => user,
        community: ({ user: { community } }) => community,
        jwt: ({ model: { jwt_token } }) => jwt_token,
        jwt_authorization: ({ model: { jwt_token } }) => `Bearer ${jwt_token}`,
        tokenAuth: ({ model: { jwt_token } }) => ({ Authorization: `Bearer ${jwt_token}` }),
    },

    // mutations
    mutations: {
        AUTHENTICATED(state) {
            state.authenticated = true;
        },

        LOADING(_, payload) {
            this.loading = payload;
        },

        LOGOUT(state) {
            // clear all models
            localStorage.removeItem('communityID');
            localStorage.removeItem('vuex');
            localStorage.removeItem('projects');
            state.user.clear();
            state.model.logout();
            state.incidentCreateAccess.clear();
            state.incidentUpdateAccess.clear();
            state.momoApiAcces.clear();
            state.exportReportsApiAcces.clear();
            state.groupAdminAccess.clear();
            state.userAdminAccess.clear();
            state.pusherConnected = false;
        },

        SET_LOGGED_IN(state, newValue) {
            state.rememberLoggedIn = newValue;
        },
    },

    // actions
    actions: {
        logIn({ commit, dispatch, getters, state, rootState }, payload) {
            const { user, password, rememberLoggedIn, clientId, clientSecret } = payload;

            // set load state
            commit('LOADING', true);
            state.authenticated = false;
            dispatch('setLoggedIn', rememberLoggedIn);

            // log in
            return state.model.login(user, password, rememberLoggedIn, clientId, clientSecret).catch(() => {
                commit('LOADING', false);
            });
        },

        setLoggedIn({ commit }, payload) {
            commit('SET_LOGGED_IN', payload);
        },

        updateToken({ state }, payload) {
            state.model.setToken(payload);
        },

        updateRefreshToken({ state }, payload) {
            state.model.setRefreshToken(payload);
        },

        loadUser({ commit, dispatch, state, getters }) {
            // use session uid
            if (state.model.uid) {
                //check if uid is same as the one saved in storage (else clear some saved data)
                if (localStorage.getItem('userID')) {
                    if (`${localStorage.getItem('userID')}` !== `${state.model.uid}`) {
                        dispatch('onLogout', {}, { root: true });
                    }
                }
                //set uid in storage to check if clearing of any storage is neccesary
                localStorage.setItem('userID', state.model.uid);
                // apply jwt to models
                state.user.setConfig('headers', getters.tokenAuth);
                state.checker.setConfig('headers', getters.tokenAuth);
                state.gdprUser.setConfig('headers', getters.tokenAuth);
                state.c_user.setConfig('headers', getters.tokenAuth);

                const proCommunity = new ProCommunityModel();
                proCommunity.setConfig('headers', getters.tokenAuth);

                const communityRequest = proCommunity.fetch().then(
                    () => proCommunity,
                    (error) => {
                        if (isForbidden(error)) return null;
                        throw error;
                    }
                );

                return Promise.all([state.c_user.fetchOwn(), communityRequest, state.gdprUser.fetch()])
                    .then(([, community]) => {
                        if (localStorage.getItem('communityID')) {
                            localStorage.removeItem('communityID');
                            localStorage.removeItem('projects');
                            localStorage.removeItem('vuex');
                        }

                        applyProSession(state.user, state.c_user, community, state.model.uid);
                        if (state.user.community.id) {
                            localStorage.setItem('communityID', state.user.community.id);
                        }

                        commit('LOADING', false);
                        dispatch('connectPusher');
                        commit('AUTHENTICATED');
                    })
                    .catch(() => {
                        dispatch('logOut');
                        commit('LOADING', false);
                    });
            } else {
                dispatch('logOut');
            }
        },

        logOut({ commit, dispatch }) {
            commit('LOGOUT');
            dispatch('filters/clearFilters', null, { root: true });
            //remove uid from localstorage
            localStorage.removeItem('userID');
        },

        checkAuthentication({ commit, dispatch, state, getters }) {
            // set load state
            commit('LOADING', true);

            // load user if session has jwt info
            const jwt = state.model.jwt_token;
            const hasUser = state.user.id && state.user.uid && state.user.changed;
            const hasCommunity = state.user.community.id && state.user.community.changed;

            // enough information, fetch session check
            if (jwt && hasUser && hasCommunity) {
                state.authenticated = true;
                return Promise.resolve();
            }
            if (jwt) {
                return dispatch('loadUser');
            }
            // needs login for access
            state.authenticated = false;
            return false;
        },

        approveGdprTerms({ state }) {
            state.gdprUser.acceptProTermsOfUse = true;
            state.gdprUser
                .patch()
                .then((data) => console.log(data))
                .catch((e) => console.log(e));
        },

        updateHideMeasurementIntro({ state }, payload) {
            state.hideMeasurementIntro = payload;
        },

        checkIncidentAccess({ state, getters }) {
            state.incidentCreateAccess.setConfig('headers', getters.tokenAuth);
            state.incidentUpdateAccess.setConfig('headers', getters.tokenAuth);
            return Promise.all([state.incidentCreateAccess.fetch(), state.incidentUpdateAccess.fetch()]);
        },

        checkMomoApi({ state, getters }) {
            state.momoApiAcces.setConfig('headers', getters.tokenAuth);
            return state.momoApiAcces.fetch();
        },

        checkExportReportsApi({ state, getters }) {
            state.exportReportsApiAcces.setConfig('headers', getters.tokenAuth);
            return state.exportReportsApiAcces.fetch();
        },

        checkGroupAdmin({ state, getters }, payload) {
            state.groupAdminAccess.setConfig('headers', getters.tokenAuth);
            state.groupAdminAccess.fetch(payload);
        },

        checkUserAdmin({ state, getters }) {
            state.userAdminAccess.setConfig('headers', getters.tokenAuth);
            state.userAdminAccess.fetch();
        },

        connectPusher({ state, commit, dispatch }) {
            if (state.pusherConnected) return;
            state.pusherConnected = true;
            const pusher = new Pusher(Config.pusher_authkey, {
                cluster: Config.pusher_cluster,
                channelAuthorization: {
                    // develop: 'https://api-develop.mijnmooiestraat.vlaanderen/pusher/auth'
                    transport: 'ajax',
                    endpoint: window.location.host.includes('pro-develop') ? 'https://api-staging.mijnmooiestraat.vlaanderen/pusher/auth' : document.location.host.includes('pro-staging') ? 'https://api-staging.mijnmooiestraat.vlaanderen/pusher/auth' : document.location.host.includes('pro-beta') || window.location.host.includes('pro-loc') ? 'https://api-beta.mijnmooiestraat.vlaanderen/pusher/auth' : 'https://api.mijnmooiestraat.vlaanderen/pusher/auth',
                    headers: {
                        Authorization: `Bearer ${state.model.jwt_token}`,
                    },
                },
            });

            const channel = pusher.subscribe(`private-notifications-${state.user.uid}`);

            channel.bind('notification', function (payload) {
                const data = notificationPayload(payload);
                const linkUrl = typeof data.link === 'string' ? data.link : data.link?.url;

                if (data.title === HEATMAP_READY_TITLE) {
                    const target = parseNotificationUrl(linkUrl);
                    const onSameView = isSameVolunteerMapView(target);
                    const onHeatmapTab = window.location.hash === '#heatmap';

                    if (onSameView) {
                        refetchHeatmapIfPossible(dispatch);
                    }

                    if (!onSameView || !onHeatmapTab) {
                        notifyHeatmapReady(data, onSameView ? '' : heatmapRouteFromUrl(target));
                    }
                    return;
                }

                showExportNotification(data);
            });
        },
    },
};
